import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, setDoc, serverTimestamp, query, orderBy, where } from 'firebase/firestore';
import Barcode from 'react-barcode';
import randomInt from 'random-int';

const StockManagement = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productCode, setProductCode] = useState('');
  const [stockAmount, setStockAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ message: '', type: '' });
  const [generatedBarcodes, setGeneratedBarcodes] = useState([]);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printedLabels, setPrintedLabels] = useState(new Set());
  const [skuHistory, setSkuHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // 🆕 Label size selection
  const [labelSize, setLabelSize] = useState('large'); // 'large' or 'small'

  // Basket management states
  const [availableBaskets, setAvailableBaskets] = useState([]);
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [showBasketModal, setShowBasketModal] = useState(false);
  const [productDimensions, setProductDimensions] = useState({ width: '', height: '', length: '' });
  const [needsDimensions, setNeedsDimensions] = useState(false);

  // Dimension management states
  const [showDimensionModal, setShowDimensionModal] = useState(false);
  const [editDimensions, setEditDimensions] = useState({ width: '', height: '', length: '' });
  const [savingDimensions, setSavingDimensions] = useState(false);

  useEffect(() => {
    loadProducts();
    loadAvailableBaskets();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, products]);

  useEffect(() => {
    if (selectedProduct) {
      loadSkuHistory(selectedProduct);
    }
  }, [selectedProduct]);

  const loadAvailableBaskets = async () => {
    try {
      const basketsRef = collection(db, 'stock_basket');
      const q = query(basketsRef, where('basket_type', '==', 'fresh'), orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);

      const basketsData = [];
      for (const docSnap of snapshot.docs) {
        const basketData = { id: docSnap.id, ...docSnap.data() };

        const stockProductsRef = collection(db, 'stock_basket', docSnap.id, 'stock_product');
        const stockSnapshot = await getDocs(stockProductsRef);

        let totalVolume = 0;
        stockSnapshot.forEach((stockDoc) => {
          const product = stockDoc.data();
          const volume = (product.width || 0) * (product.height || 0) * (product.length || 0);
          totalVolume += volume;
        });

        const basketVolume = basketData.width * basketData.height * basketData.length;
        const fillPercentage = (totalVolume / basketVolume) * 100;

        basketData.fillPercentage = fillPercentage;
        basketData.remainingVolume = basketVolume - totalVolume;

        basketsData.push(basketData);
      }

      basketsData.sort((a, b) => a.fillPercentage - b.fillPercentage);

      setAvailableBaskets(basketsData);
    } catch (error) {
      console.error('Error loading baskets:', error);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setStatus({ message: 'Loading products...', type: 'info' });

      const productsRef = collection(db, 'products');
      const snapshot = await getDocs(productsRef);

      const productsData = [];
      snapshot.forEach((doc) => {
        productsData.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setProducts(productsData);
      setFilteredProducts(productsData);
      setStatus({ message: `✓ ${productsData.length} products loaded`, type: 'success' });
    } catch (error) {
      console.error('Error loading products:', error);
      setStatus({ message: 'Error loading products: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadSkuHistory = async (product) => {
    try {
      setLoadingHistory(true);
      const skuRef = collection(db, 'products', product.productId || product.id, 'sku_stock');
      const q = query(skuRef, orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);

      const history = [];
      snapshot.forEach((doc) => {
        history.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setSkuHistory(history);
    } catch (error) {
      console.error('Error loading SKU history:', error);
      setSkuHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const filterProducts = () => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = products.filter(product =>
      product.name?.toLowerCase().includes(term) ||
      product.product_code?.toLowerCase().includes(term)
    );
    setFilteredProducts(filtered);
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    const existingCode = product.product_code || '';
    setProductCode(existingCode);

    if (!product.width || !product.height || !product.length) {
      setNeedsDimensions(true);
      setProductDimensions({
        width: product.width || '',
        height: product.height || '',
        length: product.length || ''
      });
    } else {
      setNeedsDimensions(false);
      setProductDimensions({
        width: product.width,
        height: product.height,
        length: product.length
      });
    }
  };

  const handleOpenDimensionModal = (product) => {
    setSelectedProduct(product);
    setEditDimensions({
      width: product.width || '',
      height: product.height || '',
      length: product.length || ''
    });
    setShowDimensionModal(true);
  };

  const handleSaveDimensions = async () => {
    const w = parseFloat(editDimensions.width);
    const h = parseFloat(editDimensions.height);
    const l = parseFloat(editDimensions.length);

    if (isNaN(w) || isNaN(h) || isNaN(l) || w <= 0 || h <= 0 || l <= 0) {
      setStatus({ message: 'Please enter valid dimensions (greater than 0)', type: 'error' });
      return;
    }

    try {
      setSavingDimensions(true);
      setStatus({ message: 'Saving dimensions...', type: 'info' });

      const productRef = doc(db, 'products', selectedProduct.productId || selectedProduct.id);
      await updateDoc(productRef, {
        width: w,
        height: h,
        length: l
      });

      const updatedProducts = products.map(p => {
        if ((p.productId || p.id) === (selectedProduct.productId || selectedProduct.id)) {
          return { ...p, width: w, height: h, length: l };
        }
        return p;
      });

      setProducts(updatedProducts);
      setFilteredProducts(updatedProducts.filter(product =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.product_code?.toLowerCase().includes(searchTerm.toLowerCase())
      ));

      if (selectedProduct) {
        setSelectedProduct({ ...selectedProduct, width: w, height: h, length: l });
        setProductDimensions({ width: w, height: h, length: l });
        setNeedsDimensions(false);
      }

      setStatus({ message: '✓ Dimensions saved successfully!', type: 'success' });
      setShowDimensionModal(false);

    } catch (error) {
      console.error('Error saving dimensions:', error);
      setStatus({ message: 'Error saving dimensions: ' + error.message, type: 'error' });
    } finally {
      setSavingDimensions(false);
    }
  };

  const generateBarcode = (productCode, basketNumber, serial) => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const dateStr = `${day}${month}${year}`;
    return `${productCode}0${dateStr}0${basketNumber}0${serial}`;
  };

  const validateInputs = () => {
    if (!selectedProduct) {
      setStatus({ message: 'Please select a product first', type: 'error' });
      return false;
    }

    if (!productCode || productCode.length !== 4 || !/^\d+$/.test(productCode)) {
      setStatus({ message: 'Product code must be exactly 4 digits', type: 'error' });
      return false;
    }

    const stock = parseInt(stockAmount);
    if (!stock || stock <= 0) {
      setStatus({ message: 'Stock amount must be a positive number', type: 'error' });
      return false;
    }

    if (needsDimensions) {
      const w = parseFloat(productDimensions.width);
      const h = parseFloat(productDimensions.height);
      const l = parseFloat(productDimensions.length);

      if (isNaN(w) || isNaN(h) || isNaN(l) || w <= 0 || h <= 0 || l <= 0) {
        setStatus({ message: 'Please enter valid product dimensions', type: 'error' });
        return false;
      }
    }

    const existingCode = selectedProduct.product_code;
    if (existingCode && existingCode !== productCode) {
      setStatus({
        message: `This product already has code: ${existingCode}. Cannot change!`,
        type: 'error'
      });
      return false;
    }

    return true;
  };

  const calculateProductVolume = () => {
    const w = parseFloat(productDimensions.width);
    const h = parseFloat(productDimensions.height);
    const l = parseFloat(productDimensions.length);
    return w * h * l;
  };

  const findAvailableBasket = (requiredVolume) => {
    const suitableBaskets = availableBaskets.filter(basket => {
      return basket.remainingVolume >= requiredVolume && basket.fillPercentage < 100;
    });

    return suitableBaskets.length > 0 ? suitableBaskets[0] : null;
  };

  const handleProceedWithBasket = () => {
    if (!selectedBasket) {
      setStatus({ message: 'Please select a basket', type: 'error' });
      return;
    }
    setShowBasketModal(false);
    processStockAddition();
  };

  const handleAddStock = async () => {
    if (!validateInputs()) return;

    if (needsDimensions) {
      try {
        const productRef = doc(db, 'products', selectedProduct.productId || selectedProduct.id);
        await updateDoc(productRef, {
          width: parseFloat(productDimensions.width),
          height: parseFloat(productDimensions.height),
          length: parseFloat(productDimensions.length)
        });

        selectedProduct.width = parseFloat(productDimensions.width);
        selectedProduct.height = parseFloat(productDimensions.height);
        selectedProduct.length = parseFloat(productDimensions.length);
        setNeedsDimensions(false);
      } catch (error) {
        setStatus({ message: 'Error saving dimensions: ' + error.message, type: 'error' });
        return;
      }
    }

    const singleProductVolume = calculateProductVolume();
    const totalVolumeNeeded = singleProductVolume * parseInt(stockAmount);

    const basket = findAvailableBasket(totalVolumeNeeded);

    if (!basket) {
      setStatus({
        message: 'No basket with enough space found! Please create a new fresh basket.',
        type: 'error'
      });
      return;
    }

    setSelectedBasket(basket);
    setShowBasketModal(true);
  };

  const processStockAddition = async () => {
    try {
      setLoading(true);
      setStatus({ message: 'Processing...', type: 'info' });

      const stock = parseInt(stockAmount);
      const now = new Date();
      const skuDocId = now.getTime().toString();

      const singleProductVolume = calculateProductVolume();
      let remainingStock = stock;
      let currentBasket = selectedBasket;
      const allBarcodes = [];
      let serialCounter = randomInt(1, 9999);

      while (remainingStock > 0) {
        if (!currentBasket) {
          setStatus({
            message: 'Not enough basket space! Created barcodes for available space only.',
            type: 'error'
          });
          break;
        }

        const itemsCanFit = Math.floor(currentBasket.remainingVolume / singleProductVolume);
        const itemsToAdd = Math.min(remainingStock, itemsCanFit);

        if (itemsToAdd > 0) {
          const batchBarcodes = [];
          for (let i = 0; i < itemsToAdd; i++) {
            const barcode = generateBarcode(productCode, currentBasket.basket_number, serialCounter);
            batchBarcodes.push(barcode);
            allBarcodes.push({
              barcode,
              productName: selectedProduct.name,
              basketNumber: currentBasket.basket_number,
              serial: serialCounter,
              totalStock: stock,
              id: `${skuDocId}-${serialCounter}`
            });
            serialCounter++;
          }

          for (let i = 0; i < itemsToAdd; i++) {
            const stockProductDocId = `${skuDocId}_${serialCounter - itemsToAdd + i}`;
            const stockProductRef = doc(db, 'stock_basket', currentBasket.id, 'stock_product', stockProductDocId);

            await setDoc(stockProductRef, {
              product_id: selectedProduct.productId || selectedProduct.id,
              product_name: selectedProduct.name,
              product_code: productCode,
              barcode: batchBarcodes[i],
              image: selectedProduct.imageUrl || selectedProduct.image || '',
              width: parseFloat(productDimensions.width),
              height: parseFloat(productDimensions.height),
              length: parseFloat(productDimensions.length),
              basket_number: currentBasket.basket_number,
              created_at: serverTimestamp(),
              created_datetime: now.toLocaleString('en-GB')
            });
          }

          remainingStock -= itemsToAdd;
        }

        if (remainingStock > 0) {
          await loadAvailableBaskets();
          currentBasket = findAvailableBasket(singleProductVolume);
        }
      }

      const productRef = doc(db, 'products', selectedProduct.productId || selectedProduct.id);
      const skuRef = doc(db, 'products', selectedProduct.productId || selectedProduct.id, 'sku_stock', skuDocId);

      const skuData = {
        product_code: productCode,
        stock_amount: stock,
        buscat: selectedBasket.basket_number,
        barcodes: allBarcodes.map(b => b.barcode),
        created_at: serverTimestamp(),
        created_datetime: now.toLocaleString('en-GB')
      };

      await setDoc(skuRef, skuData);

      const currentStock = selectedProduct.stock || 0;
      const updateData = {
        stock: currentStock + stock
      };

      if (!selectedProduct.product_code) {
        updateData.product_code = productCode;
      }

      await updateDoc(productRef, updateData);

      setGeneratedBarcodes(allBarcodes);

      setStatus({
        message: `✓ Successfully added ${stock} items to basket(s)!`,
        type: 'success'
      });

      setStockAmount('');
      setSelectedBasket(null);
      setPrintedLabels(new Set());
      setShowPrintPreview(true);

      loadProducts();
      loadAvailableBaskets();
      loadSkuHistory(selectedProduct);

    } catch (error) {
      console.error('Error adding stock:', error);
      setStatus({ message: 'Error: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Small Label Print Function (45mm × 35mm = 1.77" × 1.38")
  const printSmallLabel = (item) => {
    const printWindow = window.open('', '_blank', 'width=300,height=250');

    if (!printWindow) {
      alert('⚠️ Popup blocked! Please allow popups for this site and try again.');
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Small Label - ${item.barcode}</title>
        <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #f0f0f0;
            padding: 10px;
          }
          
          .small-barcode-label {
            width: 45mm;
            height: 35mm;
            background: white;
            padding: 2mm;
            box-shadow: 0 0 10px rgba(0,0,0,0.2);
            border: 1px solid #ddd;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }
          
          .barcode-container {
            text-align: center;
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
          }
          
          #barcode {
            margin: 0 auto;
          }
          
          .barcode-text {
            font-size: 8px;
            margin-top: 1mm;
            font-family: monospace;
            font-weight: bold;
          }
          
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #3b82f6;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 16px;
            font-weight: bold;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            z-index: 1000;
          }
          
          .print-button:hover {
            background: #2563eb;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
            }
            
            .small-barcode-label {
              box-shadow: none;
              border: 1px solid #ddd;
            }
            
            .print-button {
              display: none;
            }
          }
          
          @page {
            size: 45mm 35mm;
            margin: 0;
          }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">🖨️ Print Small Label</button>
        
        <div class="small-barcode-label">
          <div class="barcode-container">
            <svg id="barcode"></svg>
            <div class="barcode-text">${item.barcode}</div>
          </div>
        </div>
        
        <script>
          JsBarcode("#barcode", "${item.barcode}", {
            format: "CODE128",
            width: 1.2,
            height: 28,
            displayValue: false,
            margin: 0,
            background: "#ffffff"
          });
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      setPrintedLabels(prev => new Set([...prev, item.id]));
    }, 500);
  };

  // 🆕 Large Label Print Function (existing one)
  const printLargeLabel = (item) => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');

    if (!printWindow) {
      alert('⚠️ Popup blocked! Please allow popups for this site and try again.');
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Label - ${item.productName}</title>
        <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #f0f0f0;
            padding: 10px;
          }
          
          .barcode-label {
            width: 87.6mm;
            height: 101.6mm;
            background: white;
            padding: 3mm;
            box-shadow: 0 0 10px rgba(0,0,0,0.2);
            border: 1px solid #ddd;
            position: relative;
            display: flex;
            flex-direction: column;
          }
          
          .label-header {
            text-align: center;
            margin-bottom: 2mm;
            border-bottom: 2px solid #000;
            padding-bottom: 2mm;
          }
          
          .label-title {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 1mm;
          }
          
          .label-subtitle {
            font-size: 11px;
            margin-top: 1mm;
          }
          
          .label-divider {
            border: none;
            border-top: 1px solid #000;
            margin: 2mm 0;
          }
          
          .product-name {
            font-size: 14px;
            margin-bottom: 2mm;
            min-height: 12mm;
            line-height: 1.3;
            font-weight: 500;
          }
          
          .buscat-box {
            font-size: 15px;
            font-weight: bold;
            margin-bottom: 2mm;
            padding: 2mm;
            background: #f5f5f5;
            border: 1px solid #333;
            text-align: center;
          }
          
          .barcode-container {
            text-align: center;
            margin: 3mm 0;
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
          }
          
          #barcode {
            margin: 0 auto;
          }
          
          .barcode-text {
            font-size: 13px;
            margin-top: 2mm;
            font-family: monospace;
            font-weight: bold;
          }
          
          .contact-info {
            text-align: center;
            font-size: 9px;
            margin-top: 2mm;
            padding-top: 2mm;
            border-top: 1px solid #ddd;
          }
          
          .contact-line {
            margin: 0.5mm 0;
          }
          
          .label-footer {
            position: absolute;
            bottom: 2mm;
            left: 3mm;
            right: 3mm;
            font-size: 8px;
            display: flex;
            justify-content: space-between;
            color: #666;
          }
          
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #3b82f6;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 16px;
            font-weight: bold;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            z-index: 1000;
          }
          
          .print-button:hover {
            background: #2563eb;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
            }
            
            .barcode-label {
              box-shadow: none;
              border: 1px solid #ddd;
            }
            
            .print-button {
              display: none;
            }
          }
          
          @page {
            size: 87.6mm 101.6mm;
            margin: 0;
          }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">🖨️ Print Label</button>
        
        <div class="barcode-label">
          <div class="label-header">
            <div class="label-title">HowTech </div>
            <div class="label-subtitle">STOCK LABEL</div>
          </div>

          <div class="product-name">
            ${item.productName.length > 70 ? item.productName.substring(0, 70) + '...' : item.productName}
          </div>

          <div class="buscat-box">
            Basket: ${item.basketNumber}
          </div>

          <hr class="label-divider" />

          <div class="barcode-container">
            <svg id="barcode"></svg>
            <div class="barcode-text">${item.barcode}</div>
          </div>

          <div class="contact-info">
            <div class="contact-line">www.howtech.com.bd</div>
            <div class="contact-line">Call: +8801711893988</div>
          </div>

          <div class="label-footer">
            <span>[Item #${item.serial}/${item.totalStock}]</span>
            <span>${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })} ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
          </div>
        </div>
        
        <script>
          JsBarcode("#barcode", "${item.barcode}", {
            format: "CODE128",
            width: 1.8,
            height: 50,
            displayValue: false,
            margin: 0,
            background: "#ffffff"
          });
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      setPrintedLabels(prev => new Set([...prev, item.id]));
    }, 500);
  };

  // 🆕 Unified print function based on selected size
  const printSingleLabel = (item) => {
    if (labelSize === 'small') {
      printSmallLabel(item);
    } else {
      printLargeLabel(item);
    }
  };
  // ✅ এই function দিয়ে handlePrintAll replace করুন
  const handlePrintAll = async () => {
    if (generatedBarcodes.length === 0) {
      alert('No labels to print!');
      return;
    }

    const confirmPrint = window.confirm(
      `This will print ${generatedBarcodes.length} labels one by one.\n\n` +
      `Make sure:\n` +
      `✓ Pop-ups are allowed\n` +
      `✓ Label printer is selected as default\n` +
      `✓ Label paper (${labelSize === 'small' ? '1.77" × 1.38"' : '3.45" × 4"'}) is loaded\n\n` +
      `Continue?`
    );

    if (!confirmPrint) return;

    setStatus({
      message: `🖨️ Starting to print ${generatedBarcodes.length} labels...`,
      type: 'info'
    });

    // একটা মাত্র window খুলুন
    const printWindow = window.open('', 'PrintWindow',
      labelSize === 'small' ? 'width=300,height=250' : 'width=400,height=600'
    );

    if (!printWindow) {
      alert('⚠️ Popup blocked! Please allow popups and try again.');
      return;
    }

    let printedCount = 0;

    // Serial printing - একটার পর একটা print করুন
    for (let index = 0; index < generatedBarcodes.length; index++) {
      const item = generatedBarcodes[index];

      setStatus({
        message: `🖨️ Printing label ${index + 1}/${generatedBarcodes.length}...`,
        type: 'info'
      });

      const html = labelSize === 'small' ? `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Label ${index + 1} - ${item.barcode}</title>
        <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: white;
          }
          
          .small-barcode-label {
            width: 1.77in;
            height: 1.38in;
            background: white;
            padding: 2mm;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }
          
          .barcode-container {
            text-align: center;
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
          }
          
          #barcode {
            margin: 0 auto;
          }
          
          .barcode-text {
            font-size: 8px;
            margin-top: 1mm;
            font-family: monospace;
            font-weight: bold;
          }
          
          @media print {
            @page {
              size: 1.77in 1.38in;
              margin: 0;
            }
            
            body {
              margin: 0;
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="small-barcode-label">
          <div class="barcode-container">
            <svg id="barcode"></svg>
            <div class="barcode-text">${item.barcode}</div>
          </div>
        </div>
        
        <script>
          JsBarcode("#barcode", "${item.barcode}", {
            format: "CODE128",
            width: 1.2,
            height: 28,
            displayValue: false,
            margin: 0,
            background: "#ffffff"
          });
        </script>
      </body>
      </html>
    ` : `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Label ${index + 1} - ${item.productName}</title>
        <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: white;
          }
          
          .barcode-label {
            width: 3.45in;
            height: 4in;
            background: white;
            padding: 3mm;
            position: relative;
            display: flex;
            flex-direction: column;
          }
          
          .label-header {
            text-align: center;
            margin-bottom: 2mm;
            border-bottom: 2px solid #000;
            padding-bottom: 2mm;
          }
          
          .label-title {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 1mm;
          }
          
          .label-subtitle {
            font-size: 11px;
            margin-top: 1mm;
          }
          
          .label-divider {
            border: none;
            border-top: 1px solid #000;
            margin: 2mm 0;
          }
          
          .product-name {
            font-size: 14px;
            margin-bottom: 2mm;
            min-height: 12mm;
            line-height: 1.3;
            font-weight: 500;
          }
          
          .buscat-box {
            font-size: 15px;
            font-weight: bold;
            margin-bottom: 2mm;
            padding: 2mm;
            background: #f5f5f5;
            border: 1px solid #333;
            text-align: center;
          }
          
          .barcode-container {
            text-align: center;
            margin: 3mm 0;
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
          }
          
          #barcode {
            margin: 0 auto;
          }
          
          .barcode-text {
            font-size: 13px;
            margin-top: 2mm;
            font-family: monospace;
            font-weight: bold;
          }
          
          .contact-info {
            text-align: center;
            font-size: 9px;
            margin-top: 2mm;
            padding-top: 2mm;
            border-top: 1px solid #ddd;
          }
          
          .contact-line {
            margin: 0.5mm 0;
          }
          
          .label-footer {
            position: absolute;
            bottom: 2mm;
            left: 3mm;
            right: 3mm;
            font-size: 8px;
            display: flex;
            justify-content: space-between;
            color: #666;
          }
          
          @media print {
            @page {
              size: 3.45in 4in;
              margin: 0;
            }
            
            body {
              margin: 0;
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="barcode-label">
          <div class="label-header">
            <div class="label-title">HowTech </div>
            <div class="label-subtitle">STOCK LABEL</div>
          </div>

          <div class="product-name">
            ${item.productName.length > 70 ? item.productName.substring(0, 70) + '...' : item.productName}
          </div>

          <div class="buscat-box">
            Basket: ${item.basketNumber}
          </div>

          <hr class="label-divider" />

          <div class="barcode-container">
            <svg id="barcode"></svg>
            <div class="barcode-text">${item.barcode}</div>
          </div>

          <div class="contact-info">
            <div class="contact-line">www.howtech.com.bd</div>
            <div class="contact-line">Call: +8801711893988</div>
          </div>

          <div class="label-footer">
            <span>[Item #${item.serial}/${item.totalStock}]</span>
            <span>${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })} ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
          </div>
        </div>
        
        <script>
          JsBarcode("#barcode", "${item.barcode}", {
            format: "CODE128",
            width: 1.8,
            height: 50,
            displayValue: false,
            margin: 0,
            background: "#ffffff"
          });
        </script>
      </body>
      </html>
    `;

      // Window content update করুন
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      // Barcode load হওয়ার জন্য অপেক্ষা করুন
      await new Promise(resolve => setTimeout(resolve, 500));

      // Print করুন
      printWindow.print();

      printedCount++;

      // Mark as printed
      setPrintedLabels(prev => new Set([...prev, item.id]));

      // পরবর্তী label এর জন্য delay (printer queue এ যাওয়ার জন্য সময় দিন)
      await new Promise(resolve => setTimeout(resolve, 800));
    }

    // সব শেষে window close করুন
    printWindow.close();

    setStatus({
      message: `✓ All ${printedCount} labels printed successfully!`,
      type: 'success'
    });
  };


  const handleClosePrintPreview = () => {
    setShowPrintPreview(false);
    setGeneratedBarcodes([]);
    setPrintedLabels(new Set());
  };

  const countPrinted = () => {
    return printedLabels.size;
  };

  const printBarcodeFromHistory = (barcode, productName, buscat, serial, totalStock) => {
    const item = {
      barcode,
      productName,
      basketNumber: buscat,
      serial,
      totalStock,
      id: `history-${barcode}`
    };
    printSingleLabel(item);
  };

  // Dimension Edit Modal Component
  const DimensionEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
        <div className="p-6 border-b">
          <h3 className="text-xl font-bold text-gray-800">Edit Product Dimensions</h3>
          <p className="text-sm text-gray-600 mt-1">
            {selectedProduct?.name?.substring(0, 50)}{selectedProduct?.name?.length > 50 ? '...' : ''}
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Width (inches)
              </label>
              <input
                type="number"
                step="0.01"
                value={editDimensions.width}
                onChange={(e) => setEditDimensions({ ...editDimensions, width: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter width"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Height (inches)
              </label>
              <input
                type="number"
                step="0.01"
                value={editDimensions.height}
                onChange={(e) => setEditDimensions({ ...editDimensions, height: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter height"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Length (inches)
              </label>
              <input
                type="number"
                step="0.01"
                value={editDimensions.length}
                onChange={(e) => setEditDimensions({ ...editDimensions, length: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter length"
              />
            </div>

            {editDimensions.width && editDimensions.height && editDimensions.length && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm text-blue-800">
                  <strong>Volume:</strong> {(
                    parseFloat(editDimensions.width || 0) *
                    parseFloat(editDimensions.height || 0) *
                    parseFloat(editDimensions.length || 0)
                  ).toFixed(2)} cubic inches
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={() => {
              setShowDimensionModal(false);
              setEditDimensions({ width: '', height: '', length: '' });
            }}
            disabled={savingDimensions}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveDimensions}
            disabled={savingDimensions}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
          >
            {savingDimensions ? 'Saving...' : 'Save Dimensions'}
          </button>
        </div>
      </div>
    </div>
  );

  // Basket Selection Modal Component
  const BasketSelectionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto m-4">
        <div className="p-6 border-b sticky top-0 bg-white">
          <h3 className="text-xl font-bold text-gray-800">Select Fresh Basket</h3>
          <p className="text-sm text-gray-600 mt-1">
            Choose a basket with enough space for your stock
          </p>
        </div>

        <div className="p-6">
          {availableBaskets.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No fresh baskets available. Please create one first.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableBaskets.map((basket) => {
                const isSelected = selectedBasket?.id === basket.id;
                const isFull = basket.fillPercentage >= 100;

                return (
                  <div
                    key={basket.id}
                    onClick={() => !isFull && setSelectedBasket(basket)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition ${isFull
                      ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-50'
                      : isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-lg font-bold text-gray-800">
                          Basket #{basket.basket_number}
                        </div>
                        <div className="text-sm text-gray-600">
                          {basket.width}" × {basket.height}" × {basket.length}"
                        </div>
                      </div>
                      {isSelected && !isFull && (
                        <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                          Selected
                        </div>
                      )}
                      {isFull && (
                        <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                          Full
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div>
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Capacity Used</span>
                          <span className="font-bold">{basket.fillPercentage.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className={`h-3 rounded-full transition-all ${basket.fillPercentage >= 100
                              ? 'bg-red-500'
                              : basket.fillPercentage >= 80
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                              }`}
                            style={{ width: `${Math.min(basket.fillPercentage, 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="text-xs text-gray-600">
                        <div>Volume: {(basket.width * basket.height * basket.length).toFixed(2)} cu in</div>
                        <div>Remaining: {basket.remainingVolume.toFixed(2)} cu in</div>
                      </div>

                      <div className="text-xs text-gray-500 pt-2 border-t">
                        Created: {basket.created_at?.toDate().toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={() => {
              setShowBasketModal(false);
              setSelectedBasket(null);
            }}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleProceedWithBasket}
            disabled={!selectedBasket}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Proceed with Selected Basket
          </button>
        </div>
      </div>
    </div>
  );

  if (showPrintPreview) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Print Labels</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {generatedBarcodes.length} total labels •
                  {countPrinted()} printed •
                  {generatedBarcodes.length - countPrinted()} remaining
                </p>
              </div>
              <div className="flex gap-3 items-center">
                {/* 🆕 Label Size Selector */}
                <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg">
                  <label className="text-sm font-medium text-gray-700">Label Size:</label>
                  <select
                    value={labelSize}
                    onChange={(e) => setLabelSize(e.target.value)}
                    className="bg-white border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="large">Large (3.45" × 4")</option>
                    <option value="small">Small (1.77" × 1.38")</option>
                  </select>
                </div>

                <button
                  onClick={handlePrintAll}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-200 shadow-md"
                >
                  🖨️ Print All Labels
                </button>
                <button
                  onClick={handleClosePrintPreview}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>

          {/* Labels Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {generatedBarcodes.map((item, index) => {
              const isPrinted = printedLabels.has(item.id);

              return (
                <div
                  key={index}
                  className={`bg-white rounded-lg shadow-md overflow-hidden border-2 transition-all ${isPrinted ? 'border-green-500 opacity-75' : 'border-gray-200'
                    }`}
                >
                  {/* Mini Preview */}
                  <div className="p-4 bg-gray-50 border-b relative">
                    {isPrinted && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                        ✓ Printed
                      </div>
                    )}

                    {labelSize === 'large' ? (
                      // Large label preview
                      <>
                        <div className="text-center mb-2">
                          <div className="text-xs font-bold text-gray-800">HowTech </div>
                          <div className="text-xs text-gray-600">STOCK LABEL</div>
                        </div>
                        <div className="text-xs text-gray-700 mb-2 h-10 overflow-hidden">
                          {item.productName.substring(0, 50)}{item.productName.length > 50 ? '...' : ''}
                        </div>
                        <div className="text-xs font-bold bg-gray-200 p-1 text-center rounded mb-2">
                          Basket: {item.basketNumber}
                        </div>
                      </>
                    ) : (
                      // Small label preview - just barcode
                      <div className="text-center mb-2">
                        <div className="text-xs text-gray-600">Compact Label</div>
                      </div>
                    )}

                    <div className="flex justify-center">
                      <Barcode
                        value={item.barcode}
                        width={labelSize === 'small' ? 1 : 1}
                        height={labelSize === 'small' ? 25 : 30}
                        fontSize={8}
                        margin={0}
                        displayValue={true}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="p-3 bg-white">
                    <div className="text-xs text-gray-600 mb-2 text-center">
                      Label #{item.serial} of {item.totalStock}
                      <span className="ml-2 text-blue-600 font-semibold">
                        ({labelSize === 'small' ? '1.77" × 1.38"' : '3.45" × 4"'})
                      </span>
                    </div>
                    <button
                      onClick={() => printSingleLabel(item)}
                      className={`w-full py-2 px-4 rounded-lg font-semibold transition duration-200 ${isPrinted
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                    >
                      {isPrinted ? '🔄 Print Again' : '🖨️ Print Label'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md min-h-screen">
      {showBasketModal && <BasketSelectionModal />}
      {showDimensionModal && <DimensionEditModal />}

      <div className="px-8 py-6 border-b">
        <h2 className="text-2xl font-bold text-gray-800">Stock Management System</h2>
        <p className="text-sm text-gray-600 mt-1">Add and manage product inventory with basket allocation</p>
      </div>

      <div className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Product List */}
          <div className="bg-white border rounded-lg shadow-sm">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold mb-4">Product List</h3>

              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  placeholder="🔍 Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={loadProducts}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition duration-200"
                >
                  🔄
                </button>
              </div>

              {status.message && (
                <div className={`p-3 rounded-lg mb-4 ${status.type === 'success' ? 'bg-green-100 text-green-800' :
                  status.type === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                  {status.message}
                </div>
              )}
            </div>

            <div className="overflow-auto max-h-[600px]">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductSelect(product)}
                  className={`cursor-pointer hover:bg-gray-50 border-b p-4 transition ${selectedProduct?.id === product.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                >
                  <div className="flex gap-3">
                    <img
                      src={product.imageUrl || product.image || 'https://via.placeholder.com/60'}
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded border"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/60';
                      }}
                    />

                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-800 line-clamp-2">
                        {product.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Code: {product.product_code || 'N/A'}
                      </div>
                      <div className="text-xs font-semibold text-green-600 mt-1">
                        Stock: {product.stock || 0}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Dimensions: {product.width && product.height && product.length
                          ? `${product.width}" × ${product.height}" × ${product.length}"`
                          : 'Not set'}
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDimensionModal(product);
                      }}
                      className="text-blue-500 hover:text-blue-700 text-xs px-2 py-1 border border-blue-500 rounded hover:bg-blue-50 transition"
                      title="Edit dimensions"
                    >
                      📏 Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Middle Panel - Stock Management */}
          <div className="bg-white border rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Stock Management</h3>

            {selectedProduct ? (
              <div className="space-y-4">
                {/* Product Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex gap-3 mb-3">
                    <img
                      src={selectedProduct.imageUrl || selectedProduct.image || 'https://via.placeholder.com/100'}
                      alt={selectedProduct.name}
                      className="w-24 h-24 object-cover rounded border"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100';
                      }}
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800 mb-2">{selectedProduct.name}</h4>
                      <p className="text-sm text-green-600">Current Stock: {selectedProduct.stock || 0}</p>
                      {selectedProduct.product_code && (
                        <p className="text-sm text-gray-600">Product Code: {selectedProduct.product_code}</p>
                      )}
                      {selectedProduct.width && selectedProduct.height && selectedProduct.length && (
                        <p className="text-xs text-gray-500 mt-1">
                          Dimensions: {selectedProduct.width}" × {selectedProduct.height}" × {selectedProduct.length}"
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Product Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Code (4 digits)
                  </label>
                  <input
                    type="text"
                    value={productCode}
                    onChange={(e) => setProductCode(e.target.value)}
                    maxLength={4}
                    disabled={!!selectedProduct.product_code}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    placeholder="Enter 4-digit code"
                  />
                  {selectedProduct.product_code && (
                    <p className="text-xs text-gray-500 mt-1">Code already set (cannot change)</p>
                  )}
                </div>

                {/* Product Dimensions */}
                {needsDimensions && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                    <p className="text-sm font-semibold text-yellow-800 mb-3">
                      ⚠️ Product dimensions required for basket allocation
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Width (in)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={productDimensions.width}
                          onChange={(e) => setProductDimensions({ ...productDimensions, width: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="W"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Height (in)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={productDimensions.height}
                          onChange={(e) => setProductDimensions({ ...productDimensions, height: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="H"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Length (in)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={productDimensions.length}
                          onChange={(e) => setProductDimensions({ ...productDimensions, length: e.target.value })}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="L"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Stock Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stock Quantity
                  </label>
                  <input
                    type="number"
                    value={stockAmount}
                    onChange={(e) => setStockAmount(e.target.value)}
                    min="1"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter quantity"
                  />
                </div>

                {/* Add Stock Button */}
                <button
                  onClick={handleAddStock}
                  disabled={loading}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50"
                >
                  {loading ? 'Processing...' : '🗂️ Select Basket & Add Stock'}
                </button>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>Select a product from the list to add stock</p>
              </div>
            )}
          </div>

          {/* Right Panel - SKU History */}
          <div className="bg-white border rounded-lg shadow-sm">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">Stock History</h3>
              {selectedProduct && (
                <p className="text-xs text-gray-600 mt-1">
                  {selectedProduct.name.substring(0, 30)}{selectedProduct.name.length > 30 ? '...' : ''}
                </p>
              )}
            </div>

            <div className="overflow-auto max-h-[600px]">
              {!selectedProduct ? (
                <div className="p-8 text-center text-gray-500">
                  <p>Select a product to view stock history</p>
                </div>
              ) : loadingHistory ? (
                <div className="p-8 text-center text-gray-500">
                  <p>Loading history...</p>
                </div>
              ) : skuHistory.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p>No stock history found</p>
                </div>
              ) : (
                <div className="divide-y">
                  {skuHistory.map((sku, index) => (
                    <div key={sku.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-800">
                            Basket: {sku.buscat}
                          </div>
                          <div className="text-xs text-gray-600">
                            {sku.created_datetime || 'N/A'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-blue-600">
                            {sku.stock_amount} items
                          </div>
                          <div className="text-xs text-gray-500">
                            Code: {sku.product_code}
                          </div>
                        </div>
                      </div>

                      {sku.barcodes && sku.barcodes.length > 0 && (
                        <div>
                          <div className="text-xs font-medium text-gray-700 mb-2">
                            Barcodes ({sku.barcodes.length}):
                          </div>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {sku.barcodes.map((barcode, barcodeIndex) => (
                              <div
                                key={barcodeIndex}
                                className="flex items-center justify-between bg-white border rounded p-2 hover:bg-gray-50"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-mono text-gray-700">
                                      #{barcodeIndex + 1}
                                    </span>
                                    <span className="text-xs font-mono text-gray-900">
                                      {barcode}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => printBarcodeFromHistory(
                                    barcode,
                                    selectedProduct.name,
                                    sku.buscat,
                                    barcodeIndex + 1,
                                    sku.stock_amount
                                  )}
                                  className="ml-2 bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded transition"
                                  title="Print this barcode"
                                >
                                  🖨️
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockManagement;